import { CalcSeedMapCmd } from "../calcSeedMap/calcSeedMapCmd"
import { SeedsMap } from "../map/seedsMap"
import { Component, defineComponent, FieldViewModel, handleApiError, IOmniaCommand, IOmniaShell } from "../exports"
import { EditPlanCmd } from "../plan/edit/editPlanCmd"
import { ISeedRatePlan, ISeedsService } from "../types"
import { getSeedQtyForPlan } from "../utils"
import { str } from "@omnia/shared/strings"
import { getSeedRateUnitInfo, getTotalQtyLabel } from "../units"
import { SeedRateUnit } from "../generated/enums"
import { showDeleteCancel } from "@omnia/shared/shell/msgbox/msgbox"

export class MenuCtrl extends Component {

    static create(): ng.IDirective {

        return defineComponent(MenuCtrl, "app/seeds/menu/menu.html", {
            map: "="
        });

    };

    private map: SeedsMap;

    public planCommands: IOmniaCommand[][];

    private _calcCmd: CalcSeedMapCmd;

    private _plan: ISeedRatePlan;

    public plans: ISeedRatePlan[];

    public selectedCropping: FieldViewModel;

    private addPlanCmd: EditPlanCmd;
    private editPlanCmd: EditPlanCmd;

    static $inject = ["$scope", "OmniaSeedsService", "OmniaShell"];

    constructor(
        $scope: ng.IScope,
        public svc: ISeedsService,
        private shell: IOmniaShell,
        public readonly readOnly = shell.isReadOnly(p => p.seedPlans)) {

        super($scope);

        this._icon = "icon-seeds";
        this._title = "Seed plans";

    }

    $onInit() {

        this.setupCommands()

        this.setupMenu()

        this.$scope.$watchGroup(["vm.svc.selectedCropping", "vm.svc.selectedField"], () => {
            this.onSelectionChanged()
        })

        this.startInitialComponent()

        this.$scope.$watch("vm.svc.stateVersion", (a, b) => (a !== b) && this.setupMenu())

    }

    private startInitialComponent() {

        let params = this.shell.getRouteParams() || {};

        let component = params.component;
        var id = params.id;

        if (component == "edit-plan" && id && this.plans && this.editPlanCmd) {
            let plan = this.plans.find(p => p.SeedRatePlanId == id);
            if (plan) {
                this.editPlanCmd.execute(plan);
            }
        }
    }

    protected setupMenu() {
        this.setupPlans(true);
    }

    private setupCommands() {

        const { readOnly } = this

        this.editPlanCmd = new EditPlanCmd(this.componentContext, readOnly ? "Show plan details" : "Edit this plan", "fas fa-pen")
        this.editPlanCmd.canExecute = (plan: ISeedRatePlan) => !!plan
        this.editPlanCmd.execute = (plan: ISeedRatePlan) => this.editPlanCmd.editPlan(plan)

        this.addPlanCmd = new EditPlanCmd(this.componentContext, "Create a new plan", "addnew");
        this.addPlanCmd.execute = () => this.addPlanCmd.editPlan(null);

        this._calcCmd = new CalcSeedMapCmd(this.componentContext, "Calculate all maps", "calc")
        this._calcCmd.canExecute = (plan: ISeedRatePlan) => (!readOnly) && (!!plan) && (!plan.UseFlatRate) && plan.Maps && (plan.Maps.length > 0)
        this._calcCmd.execute = (plan: ISeedRatePlan) => this.calculateAllMaps(this.selectedPlan)

        const deleteCmd = {
            text: "Delete",
            icon: "delete",
            canExecute: (plan: ISeedRatePlan) => (!readOnly) && !!plan,
            execute: (plan: ISeedRatePlan) => this.deletePlan(plan)
        }

        this.planCommands = [
            [this.editPlanCmd, deleteCmd],
            [this._calcCmd],
        ];

    }

    private onSelectionChanged() {

        const cropping = this.svc.selectedCropping
        if (!cropping) return

        this.setCropping(cropping)

    }

    private setCropping(cropping: FieldViewModel) {

        if (!this.hasPlans()) {
            return
        }

        const { plans, selectedPlan } = this

        const selectedPlanId = selectedPlan?.SeedRatePlanId

        const matches = this.plans
            .filter(p => p.Maps && !!p.Maps.find(c => c.CroppingId == cropping.croppingId))
            .sort((a, b) => (a.SeedRatePlanId == selectedPlanId ? 0 : 1) - (b.SeedRatePlanId == selectedPlanId ? 0 : 1))

        const plan = matches[0]

        if (plan) {
            if (plan != this.selectedPlan) this.selectedPlan = plan;
            this.selectedCropping = cropping;
        }


    }

    hasPlans(): boolean {
        return this.plans && (this.plans.length > 0);
    }


    get selectedPlan(): ISeedRatePlan {
        return this._plan
    }

    set selectedPlan(value: ISeedRatePlan) {
        this.map.resetDefaultLayers()
        this._plan = this.map.currentPlan = value
        this.svc.state.plan = value
    }

    private setupPlans(restoreState: boolean) {

        this._plan = this.map.currentPlan = null

        const plans = this.plans = this.svc.getPlans()

        if (restoreState) {
            var plan = this.svc.state.plan;
            if (plan) this.selectedPlan = this.plans.find(p => p.SeedRatePlanId == plan.SeedRatePlanId);
        }

        if ((!this.selectedPlan) && (plans.length == 1)) {
            this.selectedPlan = plans[0];
        }

    }

    private calculateAllMaps(plan: ISeedRatePlan) {

        if (!plan) return;

        var cropping = this.getCroppingForPlan(plan);

        this._calcCmd.calcSeedRatesMap(plan, cropping);

    }

    private getCroppingForPlan(plan: ISeedRatePlan): FieldViewModel[] {

        return this.svc.cropping.filter(c => !!plan.Maps.find(pc => pc.CroppingId == c.croppingId));

    }

    private deletePlan(plan: ISeedRatePlan) {

        if (!plan) return;

        var msg = "Do you really want to delete this plan?";

        var p = showDeleteCancel(this.shell, msg, str.Delete);

        p.then(r => {

            if (r) {

                this.svc.deletePlan(plan.SeedRatePlanId)
                    .then(() => this.setupPlans(true))
                    .catch(e => handleApiError(this.shell, e))

            }

        });

    }

    getTotalQtyLabel(plan: ISeedRatePlan) {
        const qty = getSeedQtyForPlan(plan)
        const unit = getSeedRateUnitInfo(plan.Unit || SeedRateUnit.Kg)
        return getTotalQtyLabel(qty,unit)
    }

    getSeedQtyForPlan(plan: ISeedRatePlan) {
        return getSeedQtyForPlan(plan);
    }

}
